import { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { hrms } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import Card from '../../components/common/Card';
import Table from '../../components/common/Table';
import Badge from '../../components/common/Badge';
import Button from '../../components/common/Button';
import Modal from '../../components/common/Modal';
import { theme } from '../../theme';

function MyWFHSingleDate() {
  const { user } = useAuth();
  const location = useLocation();
  const isManager = user?.is_team_lead || user?.role === 'super_admin';
  const [activeTab, setActiveTab] = useState(location.search.includes('tab=team') ? 'Team Requests' : 'My WFH');
  const [myRequests, setMyRequests] = useState([]);
  const [teamRequests, setTeamRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [limitLoading, setLimitLoading] = useState(false);
  const [myLimit, setMyLimit] = useState({ days_per_month: 4, period_type: 'monthly' });
  const [applyModal, setApplyModal] = useState(false);
  const [form, setForm] = useState({ date: '', reason: '' });
  const [rejectModal, setRejectModal] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [wfhError, setWfhError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await hrms.getMyWFHRequests();
      const d = r.data;
      setMyRequests(Array.isArray(d) ? d : d.items || []);
    } catch {
      setMyRequests([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadTeam = useCallback(async () => {
    try {
      const r = await hrms.getTeamWFHRequests();
      const d = r.data;
      setTeamRequests(Array.isArray(d) ? d : d.items || []);
    } catch {
      setTeamRequests([]);
    }
  }, []);

  const loadLimit = useCallback(async () => {
    setLimitLoading(true);
    try {
      const r = await hrms.getMyWFHLimit();
      setMyLimit({
        days_per_month: Number(r.data?.days_per_month ?? 4),
        period_type: r.data?.period_type || 'monthly',
      });
    } catch {
      setMyLimit({ days_per_month: 4, period_type: 'monthly' });
    } finally {
      setLimitLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadLimit(); }, [loadLimit]);
  useEffect(() => { if (isManager && activeTab === 'Team Requests') loadTeam(); }, [isManager, activeTab, loadTeam]);

  const resetForm = () => { setForm({ date: '', reason: '' }); setWfhError(''); };

  const closeApplyModal = () => { setApplyModal(false); resetForm(); };

  const handleApply = async () => {
    if (!form.date || !form.reason.trim()) {
      setWfhError('Please fill all required fields.');
      return;
    }
    setWfhError('');
    setSaving(true);
    try {
      await hrms.createWFHRequest({
        date: form.date,
        reason: form.reason.trim(),
      });
      closeApplyModal();
      load();
    } catch (err) {
      setWfhError(err?.response?.data?.detail || 'Failed to submit WFH request.');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = async (id) => {
    if (!window.confirm('Cancel this WFH request?')) return;
    try {
      await hrms.cancelWFHRequest(id);
      load();
    } catch (err) {
      alert(err?.response?.data?.detail || 'Failed to cancel.');
    }
  };

  const handleApprove = async (id) => {
    try {
      await hrms.approveWFHRequest(id);
      loadTeam();
    } catch (err) {
      alert(err?.response?.data?.detail || 'Failed to approve.');
    }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) { alert('Please enter a rejection reason.'); return; }
    setSaving(true);
    try {
      await hrms.rejectWFHRequest(rejectModal, rejectReason);
      setRejectModal(null);
      setRejectReason('');
      loadTeam();
    } catch (err) {
      alert(err?.response?.data?.detail || 'Failed to reject.');
    } finally {
      setSaving(false);
    }
  };

  const myCols = [
    { header: 'Date', key: 'date', render: (v) => v ? new Date(v).toLocaleDateString() : '—' },
    { header: 'Reason', key: 'reason', render: (v) => v ? <span style={{ whiteSpace: 'normal', wordBreak: 'break-word', display: 'block', minWidth: 120 }}>{v}</span> : '—' },
    { header: 'Status', key: 'status', render: (v, row) => <Badge status={v} byName={row.approved_by_name || row.pending_approver_name} /> },
    {
      header: 'Actions', key: 'id', align: 'right',
      render: (id, row) => row.status === 'pending' ? (
        <Button size="sm" variant="danger" onClick={() => handleCancel(id)}>Cancel</Button>
      ) : null,
    },
  ];

  const teamCols = [
    { header: 'Employee', key: 'employee_name', render: (v, row) => v || row.employee?.full_name || '—' },
    { header: 'Date', key: 'date', render: (v) => v ? new Date(v).toLocaleDateString() : '—' },
    { header: 'Reason', key: 'reason', render: (v) => v ? <span style={{ whiteSpace: 'normal', wordBreak: 'break-word', display: 'block', minWidth: 120 }}>{v}</span> : '—' },
    { header: 'Status', key: 'status', render: (v, row) => <Badge status={v} byName={row.approved_by_name || row.pending_approver_name} /> },
    {
      header: 'Actions', key: 'id', align: 'right',
      render: (id, row) => row.status === 'pending' ? (
        <div style={{ display: 'flex', gap: '0.4rem', justifyContent: 'flex-end' }}>
          <Button size="sm" variant="success" onClick={() => handleApprove(id)}>Approve</Button>
          <Button size="sm" variant="danger" onClick={() => { setRejectModal(id); setRejectReason(''); }}>Reject</Button>
        </div>
      ) : null,
    },
  ];

  const inp = {
    width: '100%',
    padding: '0.6rem 0.875rem',
    border: `1.5px solid ${theme.border}`,
    borderRadius: '0.5rem',
    fontSize: '0.875rem',
    color: theme.text,
    outline: 'none',
    marginBottom: '0.75rem',
    boxSizing: 'border-box',
  };
  const lbl = { display: 'block', fontSize: '0.8rem', fontWeight: 600, color: theme.text, marginBottom: '0.35rem' };

  const tabList = ['My WFH', ...(isManager ? ['Team Requests'] : [])];
  const now = new Date();
  const requestsThisMonth = myRequests.filter((row) => {
    if (!row?.date) return false;
    if (row.status === 'rejected' || row.status === 'cancelled') return false;
    const dt = new Date(row.date);
    return dt.getFullYear() === now.getFullYear() && dt.getMonth() === now.getMonth();
  }).length;
  const availableDays = Math.max(0, Number(myLimit.days_per_month || 0) - requestsThisMonth);
  const periodLabel = myLimit.period_type === 'yearly' ? 'year' : myLimit.period_type === 'quarterly' ? 'quarter' : 'month';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
        <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: theme.text, margin: 0 }}>Work From Home</h1>
        <Button variant="primary" onClick={() => setApplyModal(true)}>+ Request WFH</Button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1rem' }}>
        <Card>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1rem' }}>
            <div>
              <div style={{ fontSize: '0.8rem', fontWeight: 700, color: theme.textMuted, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                Available WFH Days
              </div>
              <div style={{ fontSize: '2rem', fontWeight: 800, color: theme.primary, lineHeight: 1.1, marginTop: '0.4rem' }}>
                {limitLoading ? '...' : availableDays}
              </div>
              <div style={{ fontSize: '0.85rem', color: theme.textMuted, marginTop: '0.45rem' }}>
                Remaining this {periodLabel}
              </div>
            </div>
            <div style={{ width: 48, height: 48, borderRadius: '0.9rem', background: `${theme.primary}14`, color: theme.primary, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.4rem', fontWeight: 700 }}>
              H
            </div>
          </div>
          <div style={{ marginTop: '1rem', display: 'flex', justifyContent: 'space-between', gap: '0.75rem', fontSize: '0.85rem' }}>
            <span style={{ color: theme.textMuted }}>Limit: <span style={{ color: theme.text, fontWeight: 700 }}>{myLimit.days_per_month}</span></span>
            <span style={{ color: theme.textMuted }}>Used: <span style={{ color: theme.text, fontWeight: 700 }}>{requestsThisMonth}</span></span>
          </div>
        </Card>
      </div>

      <div style={{ display: 'flex', gap: '0.25rem', borderBottom: `2px solid ${theme.border}` }}>
        {tabList.map((t) => (
          <button
            key={t}
            onClick={() => setActiveTab(t)}
            style={{
              padding: '0.625rem 1.25rem',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              fontWeight: activeTab === t ? 700 : 400,
              color: activeTab === t ? theme.primary : theme.textMuted,
              borderBottom: activeTab === t ? `2px solid ${theme.primary}` : '2px solid transparent',
              marginBottom: '-2px',
              fontSize: '0.875rem',
            }}
          >
            {t}
          </button>
        ))}
      </div>

      {activeTab === 'My WFH' && (
        <Card padding="0">
          <Table columns={myCols} data={myRequests} loading={loading} emptyMessage="No WFH requests found" />
        </Card>
      )}

      {activeTab === 'Team Requests' && isManager && (
        <Card padding="0">
          <Table columns={teamCols} data={teamRequests} loading={loading} emptyMessage="No team WFH requests" />
        </Card>
      )}

      <Modal
        isOpen={applyModal}
        onClose={closeApplyModal}
        title="Request Work From Home"
        footer={<><Button variant="outlineGray" onClick={closeApplyModal}>Cancel</Button><Button variant="primary" loading={saving} onClick={handleApply}>Submit Request</Button></>}
      >
        {wfhError && (
          <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: '0.5rem', padding: '0.75rem 1rem', marginBottom: '1rem', color: '#991B1B', fontSize: '0.85rem', fontWeight: 500, lineHeight: 1.5 }}>
            ⚠️ {wfhError}
          </div>
        )}
        <div>
          <label style={lbl}>WFH Date *</label>
          <input style={inp} type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
        </div>
        <label style={lbl}>Reason *</label>
        <textarea
          style={{ ...inp, resize: 'vertical', minHeight: '90px' }}
          value={form.reason}
          onChange={(e) => setForm({ ...form, reason: e.target.value })}
          placeholder="Reason for WFH request..."
        />
      </Modal>

      <Modal
        isOpen={!!rejectModal}
        onClose={() => { setRejectModal(null); setRejectReason(''); }}
        title="Reject WFH Request"
        footer={<><Button variant="outlineGray" onClick={() => setRejectModal(null)}>Cancel</Button><Button variant="danger" loading={saving} onClick={handleReject}>Reject</Button></>}
      >
        <label style={lbl}>Reason *</label>
        <textarea
          style={{ ...inp, resize: 'vertical', minHeight: '90px' }}
          value={rejectReason}
          onChange={(e) => setRejectReason(e.target.value)}
          placeholder="Enter rejection reason..."
        />
      </Modal>
    </div>
  );
}

export default MyWFHSingleDate;
